// Copyright ©️ 2026 eVolve MEP, LLC

import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { type AxiosRequestConfig, HttpStatusCode, isAxiosError, isCancel } from 'axios';

import { extractErrorMessage } from './_helpers/extractError';
import type { StrongOmit, WithOptional, WithRequired } from './_helpers/helper-types';
import { type EvolveRoute, useBaseEvolveApi } from './_helpers/useBaseEvolveApi';

import type {
  EvolveApiBody,
  EvolveApiMethod,
  EvolveApiParams,
  EvolveApiResponse,
  EvolveApiRoute,
  RequiredRoute,
  RouteWithPathParam,
} from './ENDPOINTS';

/** Used to check if an error thrown in an axios request was due to a cancellation. */
export const isCanceledApiRequest = isCancel;

/** Used to check if an error thrown in an axios request was a 400 Bad Request, e.g. a backend validation failure. */
export const isBadRequest = (error: unknown) =>
  isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest;

/** Used to check if an error thrown in an axios request was a 404 Not Found, e.g. the entity no longer exists. */
export const isNotFound = (error: unknown) => isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound;

export type AdditionalRequestConfig<
  Method extends EvolveApiMethod,
  Route extends EvolveApiRoute<Method>,
  Params extends EvolveApiParams<Method, Route> = EvolveApiParams<Method, Route>,
> = StrongOmit<AxiosRequestConfig<EvolveApiBody<Method, Route>>, 'url' | 'params'> &
  (Route extends RouteWithPathParam
    ? {
        url: RequiredRoute<Method, Route>;
      }
    : {
        url?: Route;
      }) &
  (Partial<Params> extends Params
    ? {
        params?: Params;
      }
    : {
        params: Params;
      });

type Opts<Method extends EvolveApiMethod, Route extends EvolveApiRoute<Method>> = {
  /**
   * The default config passed to each api call.
   * To make changes per-call, pass the new config params into the `apiCall`.
   */
  defaultConfig?: WithOptional<AdditionalRequestConfig<Method, Route>, 'url'>;
  /**
   * If `true`, will show a notification for any error received from the backend.
   * The thrown error will be the parsed notification string.
   *
   * If `false`, the thrown error will be the original error object.
   *
   * @remarks Defaults to `false` for `GET` and `PAGINATED_GET`.
   * @remarks Defaults to `true` for `POST`, `PUT`, `PATCH`, and `DELETE`.
   */
  alertOnError?: boolean;
};

export type GetPaginatedOpts<Route extends EvolveApiRoute<'GET_PAGINATED'>> = (Route extends RouteWithPathParam
  ? WithRequired<Opts<'GET_PAGINATED', Route>, 'defaultConfig'>
  : Opts<'GET_PAGINATED', Route>) & {
  /** How many results to return per call. */
  perPage?: number;
};

type NoBodyApiCallArgs<
  Method extends EvolveApiMethod,
  Route extends EvolveApiRoute<Method>,
> = Route extends RouteWithPathParam
  ? readonly [config: AdditionalRequestConfig<Method, Route>, throwOnMoreRecent?: boolean]
  : readonly [config?: AdditionalRequestConfig<Method, Route>, throwOnMoreRecent?: boolean];

type WithBodyApiCallArgs<
  Method extends EvolveApiMethod,
  Route extends EvolveApiRoute<Method>,
> = Route extends RouteWithPathParam
  ? readonly [body: EvolveApiBody<Method, Route>, config: AdditionalRequestConfig<Method, Route>]
  : readonly [body: EvolveApiBody<Method, Route>, config?: AdditionalRequestConfig<Method, Route>];

type ApiCallReturn<
  Method extends EvolveApiMethod,
  Route extends EvolveApiRoute<Method>,
  ApiCallArgs extends NoBodyApiCallArgs<Method, Route> | WithBodyApiCallArgs<Method, Route>,
> = {
  /**
   * Can be used to manually call the API.
   *
   * Requires the proper URL and query parameters to be passed in.
   */
  apiCall: (...args: ApiCallArgs) => Promise<EvolveApiResponse<Method, Route>>;
  /** The returned data from this API call (if the call has been made). */
  data: EvolveApiResponse<Method, Route> | undefined;
  /** Used to update the default opts after hook instantiation. */
  setDefaultOpts: (opts: Opts<Method, Route>) => void;
  /** Whether the API call is currently in progress. */
  loading: boolean;
  /** Clears out all stored data (usually in prep for a new `apiCall`) */
  reset: () => void;
  /** Any errors returned from the backend. */
  errors: unknown | undefined;
  /** Used to cancel any in-progress API calls. */
  abortController: RefObject<AbortController>;
};

const useWrappedApiCallNoBody = <Method extends EvolveApiMethod, Route extends EvolveApiRoute<Method>>(
  method: 'get' | 'apiDelete',
  url: Route,
  defaultOpts?: Opts<Method, Route>,
): ApiCallReturn<Method, Route, NoBodyApiCallArgs<Method, Route>> => {
  const abortControllerRef = useRef(new AbortController());
  const callNumRef = useRef(0);
  // Frequently, the passed in parameters are recalculated on a rerender
  // so we need to be very careful to not rerender/recalculate when they change.
  const [initialOpts, setDefaultOpts] = useState(defaultOpts);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EvolveApiResponse<Method, Route> | undefined>(undefined);
  const [errors, setErrors] = useState<unknown>();
  const { [method]: apiMethod, showError } = useBaseEvolveApi<
    EvolveApiResponse<Method, Route>,
    EvolveApiBody<Method, Route>
  >();

  const apiCall = useCallback(
    async (
      ...[config, throwOnMoreRecent]: NoBodyApiCallArgs<Method, Route>
    ): Promise<EvolveApiResponse<Method, Route>> => {
      if (method === 'get') callNumRef.current += 1;
      const callNum = callNumRef.current;
      setErrors(undefined);
      setLoading(true);

      const constructedConfig = {
        ...initialOpts?.defaultConfig,
        ...config,
      } as AdditionalRequestConfig<Method, Route>;
      return apiMethod(config?.url ?? url, {
        ...constructedConfig,
        signal: abortControllerRef.current.signal,
      })
        .catch((err) => {
          if (!isCanceledApiRequest(err)) {
            const extractedError = extractErrorMessage(err);
            setErrors(extractedError);
            if (
              initialOpts?.alertOnError ||
              // Check false explicitly since default behavior is `true`
              (method === 'apiDelete' && initialOpts?.alertOnError !== false)
            ) {
              showError(err);
              throw extractedError;
            }
          }
          throw err;
        })
        .then((res) => {
          if (throwOnMoreRecent && method === 'get' && callNum !== callNumRef.current) {
            throw `More recent call made. ${config?.url ?? url}`;
          }
          setData(res);
          return res;
        })
        .finally(() => {
          if (callNum === callNumRef.current) {
            setLoading(false);
          }
        });
    },
    [apiMethod, initialOpts?.defaultConfig, initialOpts?.alertOnError, method, showError, url],
  );

  const reset = useCallback(() => setData(undefined), []);

  return {
    setDefaultOpts,
    apiCall,
    data,
    loading,
    reset,
    errors,
    abortController: abortControllerRef,
  };
};

const useWrappedApiCallWithBody = <Method extends EvolveApiMethod, Route extends EvolveApiRoute<Method>>(
  method: 'post' | 'patch' | 'put',
  url: EvolveRoute,
  defaultOpts?: Opts<Method, Route>,
): ApiCallReturn<Method, Route, WithBodyApiCallArgs<Method, Route>> => {
  const abortControllerRef = useRef(new AbortController());
  const [initialOpts, setDefaultOpts] = useState(defaultOpts);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EvolveApiResponse<Method, Route> | undefined>(undefined);
  const [errors, setErrors] = useState<unknown>();
  const { [method]: apiMethod, showError } = useBaseEvolveApi<
    EvolveApiResponse<Method, Route>,
    EvolveApiBody<Method, Route>
  >();

  const apiCall = useCallback(
    async (...[body, config]: WithBodyApiCallArgs<Method, Route>) => {
      setErrors(undefined);
      setLoading(true);

      const constructedConfig = {
        ...initialOpts?.defaultConfig,
        ...config,
      } as AdditionalRequestConfig<Method, Route>;
      return apiMethod(config?.url ?? url, body as EvolveApiBody<Method, Route>, {
        ...constructedConfig,
        signal: abortControllerRef.current.signal,
      })
        .then((res) => {
          setData(res);
          return res;
        })
        .catch((err) => {
          if (!isCanceledApiRequest(err)) {
            const extractedError = extractErrorMessage(err);
            setErrors(extractedError);
            // Check false explicitly since default behavior is `true`
            if (initialOpts?.alertOnError !== false) {
              showError(err);
              throw extractedError;
            }
          }
          throw err;
        })
        .finally(() => setLoading(false));
    },
    [apiMethod, initialOpts?.defaultConfig, initialOpts?.alertOnError, showError, url],
  );

  const reset = useCallback(() => setData(undefined), []);

  return {
    setDefaultOpts,
    apiCall,
    data,
    loading,
    reset,
    errors,
    abortController: abortControllerRef,
  };
};

export type EvolvePaginatedResponse<DataType> = {
  data: DataType[];
  requestedSkip: number;
  requestedTake: number;
  entireCount: number;
};

type SingleOrderBy = `${string}:${'asc' | 'desc'}`;
export type OrderBy = SingleOrderBy | SingleOrderBy[];

export type PageFetcherArgs = {
  skip?: number;
  take?: number;
  searchPhrase?: string;
  orderBy?: OrderBy | OrderBy[];
};

export type PageFetcher<Route extends EvolveApiRoute<'GET_PAGINATED'>> = (
  args: PageFetcherArgs & {
    /** If `true`, will throw instead of returning if a more recent call has been made by the time this call finishes. */
    throwOnMoreRecent?: boolean;
  },
  config: AdditionalRequestConfig<'GET_PAGINATED', Route>,
) => Promise<EvolveApiResponse<'GET_PAGINATED', Route>>;

export type WrappedPaginatedGet<Route extends EvolveApiRoute<'GET_PAGINATED'>> = {
  /** Used to update the default opts after hook instantiation. */
  setDefaultOpts: (
    defaultOpts: GetPaginatedOpts<Route>,
    /**
     * Whether or not to call `reset()` in preparation for a new call.
     *
     * @remarks If not set, will call `reset()` if the new `defaultOpts` differ from the previous one (in a deep isEqual check).
     */
    resetData?: boolean,
  ) => void;
  /**
   * Used to fetch a specific page of data.
   */
  fetchPage: PageFetcher<Route>;
  /**
   * Used to fetch the next available page of data.
   *
   * Calling this will update the `data` variable.
   */
  fetchNextPage: (
    config?: Partial<
      Omit<AxiosRequestConfig<EvolveApiBody<'GET_PAGINATED', Route>>, 'url' | 'params'> & {
        params?: Partial<EvolveApiParams<'GET_PAGINATED', Route>>;
      }
    >,
  ) => Promise<boolean>;
  /** Clears out all stored data (usually in prep for a new `fetchNextPage`) */
  reset: (refresh?: boolean) => void;
  /** The returned data from this API call (if `fetchNextPage` has been called). */
  data: EvolveApiResponse<'GET_PAGINATED', Route>['data'];
  /**
   * The total number of records available on the backend.
   * Only populated if at least one page has been fetched.
   */
  entireCount: number | undefined;
  /** Whether the API call is currently in progress. */
  loading: boolean;
  /** Used to add a search to the API call. */
  searchHandler: (searchPhrase: string) => void;
  /** Used to add a sort to the API call. */
  sortHandler: (sort: OrderBy) => void;
  /** Any errors returned from the backend. */
  errors: unknown | undefined;
  /** Used to cancel any in-progress API calls. */
  abortController: RefObject<AbortController>;
};

/**
 * Wrapper for Evolve's pagination API, used with `Multisearch` endpoints.
 */
export const useEvolvePaginatedGet = <Route extends EvolveApiRoute<'GET_PAGINATED'>>(
  url: Route,
  initialOpts?: GetPaginatedOpts<Route>,
): WrappedPaginatedGet<Route> => {
  const [searchPhrase, setSearchPhrase] = useState('');
  const [orderBy, setOrderBy] = useState<OrderBy>();
  const [defaultOpts, setDefaultOptsInner] = useState(initialOpts);

  const {
    data: _1,
    setDefaultOpts: _2,
    reset: _3,
    loading: apiCallLoading,
    apiCall,
    abortController,
    ...rest
  } = useWrappedApiCallNoBody<'GET_PAGINATED', Route>('get', url as Route);
  const loadingRef = useRef(false);
  // Tracked separately to solve some race conditions on the `loading` var
  // caused by `fetchNextPage`
  const [loading, setLoading] = useState(false);
  // Used to prevent rapid calling if we're receiving errors
  const errorCountRef = useRef(0);
  const hasMoreRef = useRef(true);
  const totalRequestedRef = useRef(0);
  // Used to make sure we don't override a newer call when an older call returns
  const numCallsRef = useRef(0);

  // `data` above only has the most recent page,
  // so we keep track of all of the concatenated pages here
  const [fullData, setFullData] = useState<WrappedPaginatedGet<Route>['data']>([]);
  const [entireCount, setEntireCount] = useState<number | undefined>();

  /**
   * Used to fetch an individual page.
   *
   * **Note:** Will not update the returned `data` from this hook.
   */
  const fetchPage = useCallback<PageFetcher<Route>>(
    async (
      {
        skip,
        // Default to 50 per page if none provided
        take = defaultOpts?.perPage ?? 50,
        searchPhrase: searchPhraseOverride = searchPhrase,
        orderBy: orderByOverride = orderBy,
        throwOnMoreRecent = false,
      },
      overrideConfig,
    ) => {
      const config = {
        ...defaultOpts?.defaultConfig,
        ...overrideConfig,
        params: {
          ...defaultOpts?.defaultConfig?.params,
          skip,
          take,
          ...(orderByOverride && orderByOverride.length > 0 ? { orderBy: orderByOverride } : {}),
          ...(searchPhraseOverride ? { searchPhrase: searchPhraseOverride } : {}),
          ...overrideConfig.params,
        },
      } as AdditionalRequestConfig<'GET_PAGINATED', Route>;

      // @ts-expect-error TS can't know that the above `config` actually satisfies GetApiCallArgs due to the ternary
      const args = [config, throwOnMoreRecent] as NoBodyApiCallArgs<Route>;
      return apiCall(...args)
        .then((res) => {
          setEntireCount(res.entireCount);
          return res;
        })
        .catch((e) => {
          errorCountRef.current += 1;
          throw e;
        });
    },
    [apiCall, defaultOpts?.defaultConfig, defaultOpts?.perPage, orderBy, searchPhrase],
  );

  /**
   * Used to fetch the next page of data.
   * To access the data itself, use the `data` field returned from the hook.
   * @returns a boolean indicating whether or not there is still additional data
   */
  const fetchNextPage = useCallback<WrappedPaginatedGet<Route>['fetchNextPage']>(
    async (config) => {
      const numCall = numCallsRef.current;
      // If we're actively loading the next page,
      // or we've retrieved all available data,
      // don't bother sending another request.
      if (loadingRef.current || !hasMoreRef.current) {
        return hasMoreRef.current;
      }
      loadingRef.current = true;
      setLoading(true);
      try {
        const res = await fetchPage(
          { skip: totalRequestedRef.current },
          (config ?? {}) as AdditionalRequestConfig<'GET_PAGINATED', Route>,
        );
        if (res && numCall === numCallsRef.current) {
          setFullData(
            (alreadyFetched) => [...alreadyFetched, ...res.data] as EvolveApiResponse<'GET_PAGINATED', Route>['data'],
          );
          const total = Math.max(res.requestedTake, res.data.length) + res.requestedSkip;
          totalRequestedRef.current = total;
          hasMoreRef.current = total < res.entireCount;
        }
        return hasMoreRef.current;
      } finally {
        if (errorCountRef.current > 5) {
          // if we've errored too many times, stop calling
          hasMoreRef.current = false;
        }
        if (numCall === numCallsRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [fetchPage],
  );

  const [needsRefresh, setNeedsRefresh] = useState(false);
  const reset = useCallback(
    (refresh = true) => {
      numCallsRef.current += 1;
      loadingRef.current = false;
      hasMoreRef.current = true;
      abortController.current.abort('API call & data reset');
      // oxlint-disable-next-line react/immutability
      abortController.current = new AbortController();
      setLoading(false);
      setFullData([]);
      setEntireCount(undefined);
      setNeedsRefresh(
        refresh &&
          // Only refresh the data if we had data to begin with
          totalRequestedRef.current > 0,
      );
      totalRequestedRef.current = 0;
    },
    [abortController],
  );

  useEffect(() => {
    if (needsRefresh) {
      void fetchNextPage();
      setNeedsRefresh(false);
    }
  }, [fetchNextPage, needsRefresh]);

  const setDefaultOpts = useCallback<WrappedPaginatedGet<Route>['setDefaultOpts']>(
    (newOpts, resetData) => {
      setDefaultOptsInner((opts) => {
        const shouldUpdate = opts !== newOpts;
        // If the user explicitly passed in `resetData`,
        // always use that value to determine if we should reset.
        // If they didn't, only reset if the data changed,
        // avoiding unnecessary network calls.
        if (resetData ?? shouldUpdate) {
          reset(false);
        }
        return shouldUpdate ? newOpts : opts;
      });
    },
    [reset],
  );

  const searchHandler = useCallback(
    (value: string) => {
      setSearchPhrase(value.trim());
      reset(false);
    },
    [reset],
  );
  const sortHandler = useCallback(
    (value: OrderBy) => {
      const valueWithoutSpaces = (Array.isArray(value) ? value : [value]).map((v) =>
        v.replaceAll(/\s/g, '').toLowerCase(),
      ) as OrderBy;
      setOrderBy(valueWithoutSpaces);
      reset(false);
    },
    [reset],
  );

  return {
    setDefaultOpts,
    fetchPage,
    fetchNextPage,
    reset,
    data: fullData,
    entireCount,
    searchHandler,
    sortHandler,
    loading: loading || apiCallLoading,
    abortController,
    ...rest,
  };
};

export type GetOpts<Route extends EvolveApiRoute<'GET'>> = Opts<'GET', Route> &
  (Route extends RouteWithPathParam
    ? {
        /** Cannot set lazy when URL has missing route parameters. */
        lazy?: boolean;
      }
    : {
        /**
         * If false, will automatically make the API call
         * on load and on every config change.
         *
         * @defaultValue true
         * */
        lazy?: boolean;
      });

/** Used to make a GET request to an EVOLVE API. */
export const useEvolveGet = <Route extends EvolveApiRoute<'GET'>>(
  /** The EVOLVE API route to call. */
  url: Route,
  /**
   * Any default opts to use in the API call.
   * To update after hook instantiation, use `setDefaultOpts` returned from this hook.
   */
  opts?: GetOpts<Route>,
): StrongOmit<ApiCallReturn<'GET', Route, NoBodyApiCallArgs<'GET', Route>>, 'setDefaultOpts'> & {
  setDefaultOpts: (newOpts: GetOpts<Route>) => void;
} => {
  const {
    apiCall,
    setDefaultOpts: setDefaultOptsInner,
    loading,
    ...rest
  } = useWrappedApiCallNoBody<'GET', Route>('get', url, opts);

  const [lazy, setLazy] = useState(opts?.lazy ?? true);
  // Used to track if the original non-lazy call has been made
  const [initialCallMade, setInitialCallMade] = useState(lazy);
  useEffect(() => {
    if (!lazy) {
      // @ts-expect-error Because `url` MUST be the parameter-required version,
      // no params are required for `apiCall`; but TS can't seem to figure that out.
      const params = [] as NoBodyApiCallArgs<'GET', Route>;
      void apiCall(...params).finally(() => setInitialCallMade(true));
    }
  }, [apiCall, lazy]);

  const setDefaultOpts = useCallback(
    (newOpts: GetOpts<Route>) => {
      setDefaultOptsInner(newOpts);
      setLazy((l) => newOpts.lazy ?? l);
    },
    [setDefaultOptsInner],
  );

  return {
    apiCall,
    setDefaultOpts,
    loading: loading || !initialCallMade,
    ...rest,
  };
};

/** Used to make a POST request to an EVOLVE API. */
export const useEvolvePost = <Route extends EvolveApiRoute<'POST'>>(
  /** The EVOLVE API route to call. */
  url: Route,
  /**
   * Any default opts to use in the API call.
   * To update after hook instantiation, use `setDefaultOpts` returned from this hook.
   */
  opts?: Opts<'POST', Route>,
): ApiCallReturn<'POST', Route, WithBodyApiCallArgs<'POST', Route>> =>
  useWrappedApiCallWithBody<'POST', Route>('post', url, opts);

/** Used to make a PATCH request to an EVOLVE API. */
export const useEvolvePatch = <Route extends EvolveApiRoute<'PATCH'>>(
  /** The EVOLVE API route to call. */
  url: Route,
  /**
   * Any default opts to use in the API call.
   * To update after hook instantiation, use `setDefaultOpts` returned from this hook.
   */
  opts?: Opts<'PATCH', Route>,
): ApiCallReturn<'PATCH', Route, WithBodyApiCallArgs<'PATCH', Route>> =>
  useWrappedApiCallWithBody<'PATCH', Route>('patch', url, opts);

/** Used to make a PUT request to an EVOLVE API. */
export const useEvolvePut = <Route extends EvolveApiRoute<'PUT'>>(
  /** The EVOLVE API route to call. */
  url: Route,
  /**
   * Any default opts to use in the API call.
   * To update after hook instantiation, use `setDefaultOpts` returned from this hook.
   */
  opts?: Opts<'PUT', Route>,
): ApiCallReturn<'PUT', Route, WithBodyApiCallArgs<'PUT', Route>> =>
  useWrappedApiCallWithBody<'PUT', Route>('put', url, opts);

/** Used to make a DELETE request to an EVOLVE API. */
export const useEvolveDelete = <Route extends EvolveApiRoute<'DELETE'>>(
  /** The EVOLVE API route to call. */
  url: Route,
  /**
   * Any default opts to use in the API call.
   * To update after hook instantiation, use `setDefaultOpts` returned from this hook.
   */
  opts?: Opts<'DELETE', Route>,
): ApiCallReturn<'DELETE', Route, NoBodyApiCallArgs<'DELETE', Route>> =>
  useWrappedApiCallNoBody<'DELETE', Route>('apiDelete', url, opts);
